import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';

// Shopify's cookie banner. Watched by element rather than by
// Shopify.customerPrivacy.shouldShowBanner(): that flag reports whether a regulation
// requires a banner, and a merchant can also choose to show one where none is required,
// so it reads false on stores that are displaying a banner right now.
const COOKIE_BANNER_SELECTOR = '#shopify-pc__banner';

// storefront-banner.js injects the banner after this component connects, so there is a
// window where "no banner on the page" and "banner has not arrived yet" look identical.
//
// The script defines window.privacyBanner when it loads, whether or not it goes on to
// show a banner, so its presence is the real signal that the decision has been made —
// no guessing at how long the network takes. Only the gap between that and its boot
// painting has to be waited out.
const PRIVACY_BANNER_POLL_MS = 50;
const PRIVACY_BANNER_BOOT_GRACE_MS = 250;

// Backstop for the script never arriving at all: a failed CDN, an offline visitor, an
// extension that blocks it. Reached only when window.privacyBanner never appears.
const COOKIE_BANNER_BACKSTOP_MS = 3000;

class GeolocationPopupComponent extends Component {
  requiredRefs = ['dialogComponent'];

  #hasLoadedContext = false;
  #dialogCloseAbortController;
  #formAbortController;
  #consentAbortController;

  connectedCallback() {
    super.connectedCallback();

    this.#dialogCloseAbortController = new AbortController();
    this.refs.dialogComponent.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, {
      signal: this.#dialogCloseAbortController.signal,
    });

    this.#consentAbortController = new AbortController();

    this.#formAbortController = new AbortController();
    this.form?.addEventListener('submit', this.#handleSubmit, { signal: this.#formAbortController.signal });
    this.emailInput?.addEventListener('input', this.#handleEmailInput, { signal: this.#formAbortController.signal });

    if (this.isDesignMode) return;

    if (!this.testCountryOverride && (this.isDismissed || this.isExcludedPath || this.isExcludedHash)) return;

    this.#loadBrowsingContext();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dialogCloseAbortController?.abort();
    this.#formAbortController?.abort();
    this.#consentAbortController?.abort();
  }

  dismissPopup = async () => {
    this.#persistDismissal();
    await this.refs.dialogComponent.closeDialog();
  };

  async #loadBrowsingContext() {
    if (this.#hasLoadedContext) return;

    this.#hasLoadedContext = true;

    // Captured once per connection. Everything downstream checks this signal rather than
    // re-reading the field, so a disconnect that happens mid-flight cannot be papered over
    // by a later reconnect installing a fresh controller.
    const { signal } = this.#consentAbortController;

    // #handleDetectedCountry is awaited, and both call sites sit inside the try: it became
    // async when the cookie-banner wait was added, and an unawaited call would turn
    // anything it throws into an unhandled rejection instead of being caught here.
    try {
      const testCountryOverride = this.testCountryOverride;
      if (testCountryOverride) {
        await this.#handleDetectedCountry(testCountryOverride, signal);
        return;
      }

      const response = await fetch(this.browsingContextSuggestionsUrl, {
        headers: {
          Accept: 'application/json',
        },
        signal,
      });

      if (!response.ok) return;

      const payload = await response.json();
      const detectedCountry =
        payload?.detected_values?.country ??
        payload?.suggestions?.[0]?.parts?.country ??
        null;

      await this.#handleDetectedCountry(detectedCountry, signal);
    } catch {
    }
  }

  async #handleDetectedCountry(detectedCountry, signal) {
    if (!detectedCountry?.handle) return;

    this.#setCountryName(detectedCountry.name || detectedCountry.handle);

    if (detectedCountry.handle.toUpperCase() === this.countryCode) return;
    if (!this.testCountryOverride && this.isDismissed) return;

    await this.#waitForCookieBanner(signal);

    // The wait is open-ended, so re-check what may have changed while it ran. The signal
    // covers the connection this flow started on; isConnected covers the element itself.
    if (signal.aborted || !this.isConnected) return;
    if (!this.testCountryOverride && this.isDismissed) return;

    this.#showPopup();
  }

  // Shopify's cookie banner is a mandatory control wherever it appears, and this popup is
  // a native dialog: it renders in the top layer, above the banner, and makes Accept and
  // Decline unclickable. Waiting also stops the form asking for an email before the
  // visitor has answered the consent question at all.
  //
  // The wait is for the banner to be *actioned*, not accepted. Declining cookies is not
  // declining to be emailed, and the form carries its own consent copy, so a visitor who
  // declines still sees the waitlist offer.
  //
  // Where no banner is shown — the US, and anywhere else one is not displayed — there is
  // nothing to wait for and the popup opens exactly as before.
  async #waitForCookieBanner(signal) {
    const banner = await this.#findCookieBanner(signal);

    if (!banner || signal.aborted) return;

    // If the banner is never actioned the popup does not open this pageview. A banner
    // carries no obligation to answer it, so an untouched one means the question is still
    // open; the popup gets its turn on a later visit, once consent has been recorded.
    //
    // That open-ended wait is why abort has to settle the promise: without it a disconnect
    // would leave this pending forever, holding its listeners and observer.
    await new Promise((resolve) => {
      if (signal.aborted) return resolve();

      signal.addEventListener('abort', () => resolve(), { once: true });
      document.addEventListener('visitorConsentCollected', () => resolve(), { once: true, signal });

      // Actioning the banner also removes it. Watched as well as the event so the popup
      // is not stranded if the event does not reach this listener.
      this.#observeDocument(
        (observer) => {
          if (document.contains(banner)) return;

          observer.disconnect();
          resolve();
        },
        signal
      );
    });
  }

  async #findCookieBanner(signal) {
    const existing = document.querySelector(COOKIE_BANNER_SELECTOR);
    if (existing) return existing;

    return new Promise((resolve) => {
      if (signal.aborted) return resolve(null);

      let bootGrace;

      const settle = (observer, banner) => {
        observer?.disconnect();
        clearTimeout(backstop);
        clearTimeout(bootGrace);
        clearInterval(poll);
        resolve(banner);
      };

      // The banner appearing is the answer, whenever it comes.
      const observer = this.#observeDocument((obs) => {
        const banner = document.querySelector(COOKIE_BANNER_SELECTOR);
        if (banner) settle(obs, banner);
      }, signal);

      signal.addEventListener('abort', () => settle(observer, null), { once: true });

      // The banner script having loaded is the answer to the opposite question: if it has
      // run and painted nothing, there is no banner to wait for.
      const checkScriptLoaded = () => {
        // bootGrace also guards against the first call and the interval both scheduling a
        // grace timer.
        if (!window.privacyBanner || bootGrace) return;

        clearInterval(poll);

        // The backstop only covers the script never arriving. Once it has, the grace timer
        // owns the outcome — leaving the backstop armed would let it fire mid-grace and
        // resolve with a banner that is still painting.
        clearTimeout(backstop);

        bootGrace = setTimeout(
          () => settle(observer, document.querySelector(COOKIE_BANNER_SELECTOR)),
          PRIVACY_BANNER_BOOT_GRACE_MS
        );
      };

      // Both handles exist before the first check, so it can clear them whatever it finds.
      const poll = setInterval(checkScriptLoaded, PRIVACY_BANNER_POLL_MS);
      const backstop = setTimeout(
        () => settle(observer, document.querySelector(COOKIE_BANNER_SELECTOR)),
        COOKIE_BANNER_BACKSTOP_MS
      );

      checkScriptLoaded();
    });
  }

  // Returns null when the signal is already aborted: MutationObserver has no signal
  // option, and an abort listener added after the fact never fires, so observing here
  // would leak an observer on document.body for the life of the page.
  #observeDocument(callback, signal) {
    if (signal.aborted) return null;

    const observer = new MutationObserver(() => callback(observer));

    observer.observe(document.body, { childList: true, subtree: true });
    signal.addEventListener('abort', () => observer.disconnect(), { once: true });

    return observer;
  }

  #showPopup() {
    this.classList.add('geolocation-popup--open');
    this.refs.dialogComponent.showDialog();
    requestAnimationFrame(() => {
      this.closeButton?.focus();
    });
  }

  #setCountryName(countryName) {
    const safeCountryName = countryName?.trim() || this.countryFallback;
    const bodyCopy = this.querySelector('[ref="bodyCopy"]');

    if (bodyCopy) {
      bodyCopy.textContent = this.bodyTemplate.replace(this.countryToken, safeCountryName);
    }
  }

  #handleDialogClose = () => {
    this.classList.remove('geolocation-popup--open');
    this.#persistDismissal();
  };

  #handleSubmit = async (event) => {
    event.preventDefault();

    const emailInput = this.emailInput;
    const submitButton = this.submitButton;

    if (!emailInput || !submitButton) return;

    if (!emailInput.checkValidity()) {
      this.#showError(emailInput.validity.valueMissing ? this.errorRequiredMessage : this.errorInvalidEmailMessage);
      return;
    }

    this.#clearError();
    this.#setFormState('submitting');
    submitButton.setAttribute('disabled', 'disabled');
    submitButton.setAttribute('aria-busy', 'true');
    this.#setStatus(this.submittingMessage);

    try {
      const email = emailInput.value.trim();
      await this.#submitToKlaviyo(email);
      emailInput.value = '';
      this.#identifyInKlaviyo(email);
      this.#setFormState('success');
      submitButton.removeAttribute('aria-busy');
    } catch (error) {
      console.error('[GeolocationPopup]', error);
      submitButton.removeAttribute('disabled');
      submitButton.removeAttribute('aria-busy');
      this.#showError(this.errorInvalidEmailMessage);
    }
  };

  async #submitToKlaviyo(email) {
    if (!this.klaviyoPublicKey || !this.klaviyoListId) {
      throw new Error('Missing Klaviyo public key or list ID');
    }

    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.klaviyoPublicKey)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2026-04-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Geolocation popup',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    properties: {
                      waitlist_tag: this.waitlistTag,
                    },
                    subscriptions: {
                      email: { marketing: { consent: 'SUBSCRIBED' } },
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: { type: 'list', id: this.klaviyoListId },
              },
            },
          },
        }),
      }
    );

    if (!response.ok) {
      const responseText = await response.text().catch(() => '');
      throw new Error(`Klaviyo ${response.status}: ${responseText}`);
    }
  }

  // Identify the browser (Klaviyo _learnq queue) so onsite events attribute to this
  // profile. Best-effort: the subscription already succeeded by this point.
  #identifyInKlaviyo(email) {
    if (!email) return;
    try {
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', { '$email': email }]);
    } catch (err) {
      console.warn('[GeolocationPopup] klaviyo identify failed', err);
    }
  }

  #handleEmailInput = () => {
    if (this.emailInput?.checkValidity()) {
      this.#clearError();
    }
  };

  #showError(message) {
    if (this.errorElement) {
      this.errorElement.textContent = message;
      this.errorElement.classList.remove('hidden');
    }

    this.emailInput?.setAttribute('aria-invalid', 'true');
    this.#setFormState('error');
  }

  #clearError() {
    if (this.errorElement) {
      this.errorElement.textContent = '';
      this.errorElement.classList.add('hidden');
    }

    this.emailInput?.removeAttribute('aria-invalid');

    if (this.form?.dataset.state !== 'success') {
      this.#setFormState('idle');
    }
  }

  #setFormState(state) {
    if (this.form) {
      this.form.dataset.state = state;
    }
  }

  #setStatus(message) {
    if (this.statusElement) {
      this.statusElement.textContent = message;
    }
  }

  #persistDismissal() {
    try {
      localStorage.setItem(this.storageKey, 'true');
    } catch {
    }
  }

  get browsingContextSuggestionsUrl() {
    const root = window.Shopify.routes.root || '/';
    const url = new URL(`${root}browsing_context_suggestions.json`, window.location.origin);

    url.searchParams.set('country[enabled]', 'true');

    return url.toString();
  }

  get bodyTemplate() {
    return decodeHtmlEntities(this.dataset.bodyTemplate || '');
  }

  get countryCode() {
    return (this.dataset.countryCode || 'US').toUpperCase();
  }

  get countryFallback() {
    return this.dataset.countryFallback || 'your country';
  }

  get countryToken() {
    return this.dataset.countryToken || '__country__';
  }

  get testCountryOverride() {
    const searchParams = new URLSearchParams(window.location.search);
    const countryHandle = searchParams.get('geo_test_country')?.trim();

    if (!countryHandle) return null;

    return {
      handle: countryHandle.toUpperCase(),
      name: getCountryDisplayName(countryHandle),
    };
  }

  get isDesignMode() {
    return this.dataset.designMode === 'true';
  }

  get isDismissed() {
    try {
      return localStorage.getItem(this.storageKey) === 'true';
    } catch {
      return false;
    }
  }

  get isExcludedPath() {
    const currentPath = normalizePath(window.location.pathname);

    return this.excludedPaths.some((pattern) => pathMatches(pattern, currentPath));
  }

  get excludedPaths() {
    return (this.dataset.excludedPaths || '')
      .split(/[\n,]+/)
      .map((path) => path.trim())
      .filter(Boolean);
  }

  // Suppresses for this pageview only when the visitor lands on a deep link such as
  // /pages/our-founder#carrie-video. Nothing is persisted, so the popup still fires
  // on the next page they visit.
  get isExcludedHash() {
    const currentHash = normalizeHash(window.location.hash);

    if (!currentHash) return false;

    return this.excludedHashes.some((pattern) => hashMatches(pattern, currentHash));
  }

  get excludedHashes() {
    return (this.dataset.excludedHashes || '')
      .split(/[\n,]+/)
      .map((hash) => hash.trim())
      .filter(Boolean);
  }

  get storageKey() {
    return this.dataset.storageKey || 'hinote-geolocation-popup-dismissed';
  }

  get form() {
    return this.querySelector('.geolocation-popup__form');
  }

  get emailInput() {
    return this.form?.querySelector('[name="contact[email]"]') ?? null;
  }

  get submitButton() {
    return this.form?.querySelector('.geolocation-popup__submit') ?? null;
  }

  get closeButton() {
    return this.querySelector('.geolocation-popup__close');
  }

  get errorElement() {
    return this.form?.querySelector('.geolocation-popup__form-message--error') ?? null;
  }

  get statusElement() {
    return this.form?.querySelector('.geolocation-popup__status') ?? null;
  }

  get errorInvalidEmailMessage() {
    return this.form?.dataset.errorInvalidEmail || 'Enter a valid email address.';
  }

  get errorRequiredMessage() {
    return this.form?.dataset.errorRequired || 'Enter your email address.';
  }

  get submittingMessage() {
    return this.form?.dataset.submittingMessage || 'Submitting...';
  }

  get klaviyoPublicKey() {
    return this.form?.dataset.klaviyoPublicKey || '';
  }

  get klaviyoListId() {
    return this.form?.dataset.klaviyoListId || '';
  }

  get waitlistTag() {
    return this.form?.dataset.waitlistTag || 'geolocation-waitlist';
  }
}

function decodeHtmlEntities(value) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;

  return textarea.value;
}

function normalizePath(path) {
  if (!path) return '/';

  let normalized = path.trim();

  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }

  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
}

function pathMatches(pattern, currentPath) {
  const normalizedPattern = normalizePath(pattern);

  if (normalizedPattern.endsWith('*')) {
    return currentPath.startsWith(normalizedPattern.slice(0, -1));
  }

  return currentPath === normalizedPattern;
}

function normalizeHash(hash) {
  return (hash || '').trim().replace(/^#+/, '').toLowerCase();
}

function hashMatches(pattern, currentHash) {
  const normalizedPattern = normalizeHash(pattern);

  if (!normalizedPattern) return false;

  if (normalizedPattern.endsWith('*')) {
    return currentHash.startsWith(normalizedPattern.slice(0, -1));
  }

  return currentHash === normalizedPattern;
}

function getCountryDisplayName(countryHandle) {
  const normalizedCountryHandle = countryHandle?.trim().toUpperCase();
  if (!normalizedCountryHandle) return '';

  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') {
    return normalizedCountryHandle;
  }

  try {
    const documentLanguage = document.documentElement.lang || 'en';
    const displayNames = new Intl.DisplayNames([documentLanguage], { type: 'region' });

    return displayNames.of(normalizedCountryHandle) || normalizedCountryHandle;
  } catch {
    return normalizedCountryHandle;
  }
}

if (!customElements.get('geolocation-popup-component')) {
  customElements.define('geolocation-popup-component', GeolocationPopupComponent);
}
